import { DashboardPeriod, DashboardSpan, ICurrentMonthStats } from 'src/types';

/** `t()` as `useLocales` returns it — declared here so the two helpers below share one shape. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * NAT-2334 — the span a Termometro payload actually covers.
 *
 * An absent `period` means a backend that predates this field: it ignored `?period=` and
 * answered with the month regardless. That is a real, deployable state — the two repos ship
 * independently and in either order — so it reads as `mtd` rather than as "unknown".
 *
 * It lives in one exported helper because the view and the cards both need it, and the whole
 * point of echoing the span back is that every consumer agrees on what it is. Two files
 * spelling this `?? 'mtd'` separately is how they end up disagreeing about `undefined`.
 */
export const servedPeriodOf = (data?: ICurrentMonthStats): DashboardPeriod => data?.period ?? 'mtd';

/**
 * NAT-2545 — each period's name, as a key in the `operativaDashboard` namespace. One map for
 * every surface that names a period (the toggle, the fallback caption, the warning below), so a
 * third period cannot be named on one of them and fall through to "month" on another — which
 * is exactly what the `ytd ? … : month` ternaries this replaced would have done with `l12m`.
 */
export const PERIOD_LABEL_KEY: Record<DashboardPeriod, string> = {
  mtd: 'mese_corrente',
  ytd: 'anno_corrente',
  l12m: 'ultimi_12_mesi',
};

/**
 * "The period you picked is not available: the figures shown cover <served>." Keyed by the
 * period that WAS served, never the one asked for — the warning's whole job is to name what is
 * actually on screen.
 */
export const PERIOD_UNAVAILABLE_KEY: Record<DashboardPeriod, string> = {
  mtd: 'periodo_non_disponibile_mese',
  ytd: 'periodo_non_disponibile_anno',
  l12m: 'periodo_non_disponibile_12_mesi',
};

/**
 * The distinct-customers card is the one card whose label names its own span: "Clienti
 * mensili" beside a year's or twelve months' figure would misstate it.
 */
export const CUSTOMERS_LABEL_KEY: Record<DashboardPeriod, string> = {
  mtd: 'clienti_mensili',
  ytd: 'clienti_annuali',
  l12m: 'clienti_12_mesi',
};

/**
 * A `yyyy-MM-dd` bound as a LOCAL calendar day, or null if it is not one.
 *
 * `new Date('2026-01-01')` is UTC midnight, and every getter that follows reads it locally —
 * which renders "31 dic 2025" for any viewer west of UTC. Splitting the string keeps the day
 * the backend named.
 *
 * The validation is not defensive decoration. This is network data, and both ways it can go
 * wrong are bad in their own way:
 *
 *  - `Intl.DateTimeFormat.format()` THROWS a RangeError on an unparseable value, and this runs
 *    during render, so one malformed bound takes down the whole Operativa page — five sections,
 *    not just this panel.
 *  - the Date constructor silently rolls overflow forward: `2026-13-01` becomes 1 Jan 2027 and
 *    `2026-02-30` becomes 2 March. A caption that quietly names the wrong span is precisely the
 *    defect this caption exists to prevent, and it would be invisible.
 *
 * So the day is round-tripped back out: anything the constructor moved was not a real date.
 */
const parseYmd = (ymd: string): Date | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;

  const [year, month, day] = ymd.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);

  const isTheDayItNamed =
    parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day;

  return isTheDayItNamed ? parsed : null;
};

/**
 * The span, written out — "1 gen – 26 ago 2026".
 *
 * This is the panel's real label. Seven of the eight cards read the same either way
 * ("Ricavi", "Margine"), so without it the ONLY thing naming their span is the toggle — and
 * the toggle states what was ASKED for, which is not always what was served. Rendering the
 * served window means a figure cannot be misread even when the request failed or the backend
 * ignored it.
 */
export const formatSpan = (window: ICurrentMonthStats['window'], lang: string): string | null => {
  const start = window?.startDate ? parseYmd(window.startDate) : null;
  const end = window?.endDate ? parseYmd(window.endDate) : null;

  // Both or neither. Half a span read as a whole one would misstate it, and saying nothing
  // costs only the caption — the toggle and the one period-dependent card label remain.
  if (!start || !end) return null;

  // 'en-US', matching the app's own declared English locale (`locales-config.ts`) and the
  // sibling Intl.NumberFormat calls on these same cards. 'en-GB' would make this the one
  // day-first date in the product.
  //
  // Built per call, and that is load-bearing for the timezone test below it: changing
  // `process.env.TZ` mid-run affects only formatters constructed AFTER the change, so hoisting
  // this to module scope would make that test pass with the bug present. If it is ever
  // memoised for performance, the test must pin the zone explicitly instead.
  const formatter = new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

/**
 * The same span, compactly — "1–22 set" within one month, "28 ago – 22 set" across two, with
 * the year appended only when the range itself crosses one. `formatSpan` above always spells
 * the year out because it stands alone as the panel's only label; this one feeds a caption that
 * also carries a day count and, in a comparison, a second span right next to it — spelling the
 * year on both sides of an obviously-this-year range would be noise, not information.
 */
const formatCompactRange = (start: Date, end: Date, lang: string): string => {
  const locale = lang === 'en' ? 'en-US' : 'it-IT';
  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(end);
    return `${start.getDate()}–${end.getDate()} ${month}`;
  }

  const formatter = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' as const }),
  });
  return `${formatter.format(start)} – ${formatter.format(end)}`;
};

/** A whole calendar day, for the day-count arithmetic below. */
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The inclusive calendar-day count between two already-parsed bounds — "1 to 22 Sep" is 22
 * days, not 21. `Date.UTC` on the bounds' own year/month/date fields, not a plain subtraction
 * of the local `Date` objects: a DST transition shifts a local timestamp by an hour, which is
 * nowhere near a full day and would not normally break the day count, but a boundary date
 * sitting exactly on the transition could still round to the wrong whole number. UTC has none.
 */
const inclusiveDayCount = (start: Date, end: Date): number =>
  Math.round(
    (Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) -
      Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())) /
      DAY_MS,
  ) + 1;

/**
 * NAT-2481 — the owner's ask: every comparison the Termometro shows must carry both sides' day
 * counts, e.g. "1–22 set · 22 gg". Null on anything unparseable, nonsensical or partial, same
 * contract as `formatSpan`, so the caller can fall back to whatever caption an older backend
 * still gets.
 *
 * "Nonsensical" is checked explicitly rather than left to whatever `formatCompactRange` happens
 * to do with it: `days` must be a positive whole number (0, a negative count, or a fraction
 * from a backend miscalculation are not a day count); `start` must not be after `end` — a
 * reversed span would still format, just backwards, and reads as a real one; and `days` must
 * actually be the inclusive day count between the two bounds — a backend that sends a real span
 * with the wrong count attached is worse than one that sends nothing, because it reads as
 * verified.
 */
export const formatSpanWithDays = (
  span: DashboardSpan | undefined,
  lang: string,
  t: Translate,
): string | null => {
  if (!span || !Number.isInteger(span.days) || span.days <= 0) return null;
  const start = parseYmd(span.startDate);
  const end = parseYmd(span.endDate);
  if (!start || !end || start.getTime() > end.getTime()) return null;
  if (span.days !== inclusiveDayCount(start, end)) return null;

  return t('span_con_giorni', { range: formatCompactRange(start, end, lang), days: span.days });
};

/**
 * The full comparison caption — "1–22 set · 22 gg vs 1–22 ago · 22 gg" — both sides carrying
 * their day count. Null when either span is missing or unparseable, so the caller falls back
 * to the plain "vs anno scorso" caption an older backend, or a comparison that simply is not
 * present, still gets.
 */
export const formatSpanComparison = (
  current: DashboardSpan | undefined,
  previous: DashboardSpan | undefined,
  lang: string,
  t: Translate,
): string | null => {
  const currentText = formatSpanWithDays(current, lang, t);
  const previousText = formatSpanWithDays(previous, lang, t);
  if (!currentText || !previousText) return null;

  return t('span_confronto', { current: currentText, previous: previousText });
};
